import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetTransactionsQuery,
    type TransactionsFilterInput,
    TransactionState,
    PaymentMethodProvider,
} from 'data/__generated__';
import { textColumn, multiSelectColumn, dateRangeColumn, numberRangeColumn } from 'components/DataTable';
import { formatPrice } from 'utils/price';

import { StatusTag } from '../components/StatusTag';
import { TRANSACTIONS_STATES_LABELS } from './states';
import { DetailLink } from 'components/DetailsView/DetailLink';

export type TransactionRow = GetTransactionsQuery['transactions']['transactions'][number];

export const transactionsColumns: ColumnDef<TransactionRow, unknown>[] = [
    dateRangeColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        filterKey: 'createdAt',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        id: 'transactionType',
        accessorFn: (row) => row.nuBasket?.type ?? (row.basketId ? 'Sensei' : '-'),
        header: 'Type',
        defaultVisible: true,
        cell: ({ row }) => {
            const { nuBasketId, nuBasket, basketId } = row.original;

            if (nuBasketId) {
                const basketTypeLabel = nuBasket?.type;
                if (!basketTypeLabel) return '-';
                return <DetailLink path="basket" name={basketTypeLabel} value={nuBasketId} />;
            }

            if (basketId) {
                return <DetailLink path="sensei-basket" name="Sensei" value={basketId} />;
            }

            return '-';
        },
    }),
    multiSelectColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'newState',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(TransactionState).map((state) => ({
            value: state,
            label: TRANSACTIONS_STATES_LABELS[state],
        })),
        renderOption: (option) => <StatusTag state={option.value as TransactionState} />,
        cell: ({ row }) => <StatusTag state={row.original.newState} />,
    }),
    multiSelectColumn<TransactionRow, TransactionsFilterInput>({
        id: 'provider',
        accessorFn: (row) => row.paymentMethods?.[0]?.provider ?? '-',
        header: 'Provider',
        filterKey: 'provider',
        defaultVisible: true,
        options: Object.values(PaymentMethodProvider).map((p) => ({ value: p, label: p })),
        cell: ({ row }) => row.original.paymentMethods?.[0]?.provider ?? '-',
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'siteName',
        header: 'Site',
        filterKey: 'siteName',
        defaultVisible: true,
        cell: ({ row }) => (
            <DetailLink path="site" name={row.original.siteName} value={row.original.siteId ?? 'Pas de site'} />
        ),
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'microstoreColumnName',
        header: 'Colonne',
        filterKey: 'columnName',
        defaultVisible: true,
        cell: ({ row }) => (
            <DetailLink
                path="microstore-column"
                name={row.original.microstoreColumnName}
                value={row.original.microstoreColumnId ?? 'Pas de colonne'}
            />
        ),
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'userFullName',
        header: 'Utilisateur',
        filterKey: 'userName',
        defaultVisible: true,
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'productName',
        header: 'Produit',
        defaultVisible: true,
    }),
    numberRangeColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'priceHT',
        header: 'Prix HT',
        filterKey: 'priceHT',
        defaultVisible: true,
        cell: ({ row }) => formatPrice(row.original.priceHT),
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        id: 'priceTTC',
        accessorFn: (row) => row.priceHT * (1 + row.tva),
        header: 'Montant TTC',
        defaultVisible: true,
        cell: ({ row }) => formatPrice(row.original.priceHT * (1 + row.original.tva)),
    }),
];
